/**
 * Notes Service
 * API operations for note management
 */
import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import type {
  CreateNoteRequest,
  UpdateNoteRequest,
  NoteFiltersParams,
  MapNotesParams,
} from '@/types/api/requests';
import type {
  NoteResponse,
  NoteListResponse,
  NoteMapResponse,
  NoteImageUploadResponse,
  CreateNoteApiResponse,
  UpdateNoteApiResponse,
  AddNoteAttachmentApiResponse,
} from '@/types/api/responses';

export const notesService = {
  /**
   * Create a new note
   */
  create: (data: CreateNoteRequest) =>
    apiClient.post<CreateNoteApiResponse>(API_ENDPOINTS.NOTES.BASE, data),

  /**
   * Get all notes with optional filters and pagination
   */
  getAll: (filters?: NoteFiltersParams) =>
    apiClient.get<NoteListResponse>(API_ENDPOINTS.NOTES.BASE, {
      params: filters,
    }),

  /**
   * Get notes for map view within geographic bounds
   */
  getForMap: (params: MapNotesParams) =>
    apiClient.get<NoteMapResponse[]>(API_ENDPOINTS.NOTES.MAP, { params }),

  /**
   * Get note by ID with all relations
   */
  getById: (id: string) =>
    apiClient.get<NoteResponse>(API_ENDPOINTS.NOTES.BY_ID(id)),

  /**
   * Get note by short ID (NanoID) with all relations
   */
  getByShortId: (shortId: string) =>
    apiClient.get<NoteResponse>(API_ENDPOINTS.NOTES.BY_SHORT_ID(shortId)),

  /**
   * Update note properties
   */
  update: (id: string, data: UpdateNoteRequest) =>
    apiClient.put<UpdateNoteApiResponse>(API_ENDPOINTS.NOTES.BY_ID(id), data),

  /**
   * Delete note by ID
   */
  delete: (id: string) => apiClient.delete<void>(API_ENDPOINTS.NOTES.BY_ID(id)),

  /**
   * Upload a file attachment to a note
   */
  uploadAttachment: (noteId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<AddNoteAttachmentApiResponse>(
      API_ENDPOINTS.NOTES.ATTACHMENTS(noteId),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  /**
   * Same-origin URL of the note attachment download endpoint. Use with
   * `<a href download>` so the browser follows the 302 to the storage signed
   * URL natively (no CORS).
   */
  getAttachmentDownloadUrl: (attachmentId: string) =>
    API_ENDPOINTS.NOTES.DOWNLOAD_ATTACHMENT(attachmentId),

  /**
   * Same-origin streaming preview — bytes for in-browser viewers, bypasses the
   * cross-origin redirect so axios arraybuffer works without bucket CORS.
   */
  fetchAttachmentPreview: (attachmentId: string) =>
    apiClient.get<ArrayBuffer>(
      API_ENDPOINTS.NOTES.PREVIEW_ATTACHMENT(attachmentId),
      { responseType: 'arraybuffer' }
    ),

  /**
   * Delete a file attachment from a note
   */
  deleteAttachment: (noteId: string, attachmentId: string) =>
    apiClient.delete<void>(
      API_ENDPOINTS.NOTES.DELETE_ATTACHMENT(noteId, attachmentId)
    ),

  /**
   * Upload an inline image for note content
   */
  uploadImage: (noteId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<NoteImageUploadResponse>(
      API_ENDPOINTS.NOTES.UPLOAD_IMAGE(noteId),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  /**
   * Get notes linked to a specific task
   */
  getByTaskId: (taskId: string) =>
    apiClient.get<NoteResponse[]>(API_ENDPOINTS.NOTES.BY_TASK_ID(taskId)),
};
